import { AnimatePresence, motion } from "motion/react";

import type { FAQItem } from "@/landing/lib/types";
import { cn } from "@/common/utils";
import { Button } from "@/components/ui/Button";
import { useAppTheme } from "@/hooks/useAppTheme";

export function FaqItem({
  item,
  isOpen,
  isFirst,
  onToggle,
  onPriorityAccess,
}: {
  item: FAQItem;
  isOpen: boolean;
  isFirst: boolean;
  onToggle: () => void;
  /** Opens the email-capture dialog for items with a `priorityAccessSource`. */
  onPriorityAccess?: () => void;
}) {
  const isLight = useAppTheme() === "steward";

  return (
    <div
      className={cn(
        "transition-colors duration-200",
        !isFirst && "border-t border-[var(--cli-border)]",
        isLight
          ? isOpen
            ? "bg-[var(--cli-bg-hover)]"
            : "bg-transparent hover:bg-[var(--cli-bg-hover)]"
          : isOpen
            ? "bg-[var(--cli-bg-elevated)]/70 backdrop-blur-sm"
            : "bg-transparent hover:bg-[var(--cli-bg-elevated)]/50",
      )}
    >
      {/* The toggle is a real button scoped to the question row, so the
          priority-access Button in the answer never nests inside another
          interactive control. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-6 py-5 text-left"
      >
        <h3
          className="text-sm font-medium select-none sm:text-base"
          style={{ color: "var(--cli-text)" }}
        >
          {item.question}
        </h3>
        <motion.svg
          className="size-5 shrink-0"
          style={{
            color: isOpen ? "var(--cli-accent)" : "var(--cli-text-muted)",
          }}
          viewBox="0 0 20 20"
          fill="none"
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          <path
            d="M10 4v12M4 10h12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.svg>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { type: "spring", stiffness: 700, damping: 50 },
              opacity: { duration: 0.2 },
            }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5">
              <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--cli-text-secondary)" }}
              >
                {item.answer}
              </p>
              {item.priorityAccessSource && onPriorityAccess && (
                <div className="pt-3">
                  <Button
                    type="button"
                    variant={isLight ? "steward" : "wondercat"}
                    size="sm"
                    onClick={onPriorityAccess}
                  >
                    Get priority access
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
